import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Pagination,
  Tabs,
  Tab,
  Paper,
} from '@mui/material'
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { enrollmentService } from '../services/enrollmentService'
import { Enrollment } from '../types/enrollment'
import { EnrollmentCard } from '../components/enrollment/EnrollmentCard'
import { TutorialModal, useMyEnrollmentsTutorialSteps } from '../components/tutorial'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`enrollment-tabpanel-${index}`}
      aria-labelledby={`enrollment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: { xs: 2, sm: 3 } }}>{children}</Box>}
    </div>
  )
}

export const MyEnrollmentsPage: React.FC = () => {
  const { t } = useTranslation('enrollments')
  const { t: tTutorial } = useTranslation('tutorial')
  const [enrollments, setEnrollments] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [selectedTab, setSelectedTab] = useState(0)

  // チュートリアル関連の状態（手動表示のみ）
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialSteps = useMyEnrollmentsTutorialSteps()

  const limit = 12

  const tabs = useMemo(
    () => [
      { label: t('tabs.all'), value: '', noDataKey: 'default', icon: <AssignmentIcon /> },
      {
        label: t('tabs.assigned'),
        value: 'ASSIGNED',
        noDataKey: 'assigned',
        icon: <ScheduleIcon />,
      },
      {
        label: t('tabs.inProgress'),
        value: 'IN_PROGRESS',
        noDataKey: 'inProgress',
        icon: <ScheduleIcon />,
      },
      {
        label: t('tabs.completed'),
        value: 'COMPLETED',
        noDataKey: 'completed',
        icon: <CheckCircleIcon />,
      },
      { label: t('tabs.overdue'), value: 'OVERDUE', noDataKey: 'overdue', icon: <WarningIcon /> },
    ],
    [t]
  )

  // データ読み込み
  const loadEnrollments = React.useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const currentStatus = tabs[selectedTab]?.value || ''

      console.log('受講データ読み込み開始:', { page, selectedTab, currentStatus })

      const response = await enrollmentService.getMyEnrollments({
        status: currentStatus || undefined,
        page,
        limit,
      })

      setEnrollments(response.enrollments)
      setTotalPages(response.pagination.pages)

      console.log('受講データ読み込み完了:', {
        count: response.enrollments.length,
        totalPages: response.pagination.pages,
      })
    } catch (err: any) {
      console.error('Enrollments loading failed:', err)
      setError(t('loadError'))
    } finally {
      setLoading(false)
    }
  }, [page, selectedTab, limit, tabs, t]) // 依存配列を明示的に指定

  useEffect(() => {
    loadEnrollments()
  }, [loadEnrollments])

  // チュートリアルを閉じる
  const handleCloseTutorial = () => {
    setShowTutorial(false)
  }

  // タブ変更
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
    setPage(1) // タブ変更時はページを1にリセット
  }

  // 統計情報の計算（メモ化）
  const statusCounts = React.useMemo(() => {
    const counts = {
      total: enrollments.length,
      assigned: 0,
      inProgress: 0,
      completed: 0,
      overdue: 0,
    }

    enrollments.forEach((enrollment) => {
      switch (enrollment.status) {
        case 'ASSIGNED':
          counts.assigned++
          break
        case 'IN_PROGRESS':
          counts.inProgress++
          break
        case 'COMPLETED':
          counts.completed++
          break
        case 'OVERDUE':
          counts.overdue++
          break
      }
    })

    return counts
  }, [enrollments])

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        overflowX: 'hidden',
      }}
    >
      {/* チュートリアルモーダル（手動表示） */}
      <TutorialModal
        open={showTutorial}
        onClose={handleCloseTutorial}
        onComplete={handleCloseTutorial}
        title={tTutorial('myEnrollments.title')}
        steps={tutorialSteps}
      />

      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('title')}
      </Typography>

      {/* タブ */}
      <Paper sx={{ mb: { xs: 2, sm: 3 } }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} icon={tab.icon} label={tab.label} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ローディング */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* タブパネル */}
          {tabs.map((tab, index) => (
            <TabPanel key={index} value={selectedTab} index={index}>
              {enrollments.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    {t(`noData.${tab.noDataKey}`)}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* 受講一覧 */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: { xs: 1, sm: 3 },
                    }}
                  >
                    {enrollments.map((enrollment) => (
                      <EnrollmentCard key={enrollment.id} enrollment={enrollment} />
                    ))}
                  </Box>

                  {/* ページネーション */}
                  {totalPages > 1 && (
                    <Box display="flex" justifyContent="center" mt={4}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(e, value) => setPage(value)}
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              )}
            </TabPanel>
          ))}

          {/* 統計情報 */}
          {enrollments.length > 0 && selectedTab === 0 && (
            <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                {t('statistics.title')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: { xs: 2, sm: 2 },
                }}
              >
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color="primary"
                    sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                  >
                    {statusCounts.total}
                  </Typography>
                  <Typography variant="caption">{t('statistics.total')}</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color="primary.main"
                    sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                  >
                    {statusCounts.completed}
                  </Typography>
                  <Typography variant="caption">{t('statistics.completed')}</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color="secondary.main"
                    sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                  >
                    {statusCounts.assigned + statusCounts.inProgress}
                  </Typography>
                  <Typography variant="caption">{t('statistics.incomplete')}</Typography>
                </Box>
                <Box textAlign="center">
                  <Typography
                    variant="h4"
                    color="error.main"
                    sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
                  >
                    {statusCounts.overdue}
                  </Typography>
                  <Typography variant="caption">{t('statistics.overdue')}</Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  )
}
